package com.downdogapp.client.singleton

import com.downdogapp.client.MANIFEST
import com.downdogapp.client.singleton.CastState.*
import com.downdogapp.client.singleton.IdleReason.*
import com.downdogapp.client.singleton.PlayerState.*
import kotlin.js.Promise

actual object CastHelper : CastHelperInterface {

  override var eventHandler: CastEventHandler? = null

  override val isLoading get() = false

  private val isCastAvailable: Boolean
    // need to use typeof to avoid throwing a ReferenceError in Safari
    get() = js("typeof cast") != "undefined"
            && js("typeof chrome") != "undefined"
            && js("typeof chrome.cast") != "undefined"

  private val context
    get() =
      if (isCastAvailable) js("cast.framework.CastContext.getInstance()") else null
  private val session get() = context?.getCurrentSession() // observationally, null while CONNECTING, non-null if CONNECTED and for a few seconds after
  private var remotePlayerController: dynamic = null
  private var remotePlayerListener: dynamic = null

  override val castState: CastState
    get() = when (context?.getCastState()) {
      null, "NO_DEVICES_AVAILABLE" -> NO_DEVICES_AVAILABLE
      "NOT_CONNECTED" -> NOT_CONNECTED
      "CONNECTING" -> CONNECTING
      "CONNECTED" -> CONNECTED
      else -> error("CastState is an unrecognized string: ${context?.getCastState()}")
    }

  override val playerState: PlayerState
    get() = when (val state = session?.getMediaSession()?.playerState) {
      "PLAYING" -> PLAYING
      "PAUSED" -> PAUSED
      "BUFFERING" -> BUFFERING
      "IDLE" -> NO_MEDIA
      null -> NO_PLAYER
      else -> error("PlayerState is an unrecognized string: $state")
    }

  override val idleReason
    get() = when (val reason = session?.getMediaSession()?.idleReason) {
      "CANCELLED" -> CANCELLED
      "INTERRUPTED" -> INTERRUPTED
      "FINISHED" -> FINISHED
      "ERROR" -> ERROR
      null -> NOT_IDLE
      else -> error("IdleReason is an unrecognized string: $reason")
    }

  override val receiverName: String? get() = session?.getCastDevice()?.friendlyName // non-null if session is non-null

  override var playerTime: Double?
    get() = when (playerState) {
      PLAYING, PAUSED -> remotePlayerListener?.currentTime as? Double
      BUFFERING, NO_MEDIA, NO_PLAYER -> null
    }
    set(value) {
      checkNotNull(value) { "Cannot set playerTime to null" }
      remotePlayerListener?.currentTime = value
      remotePlayerController?.seek()
    }

  init {
    if (isCastAvailable) {
      context.addEventListener(
        js("cast.framework.CastContextEventType.CAST_STATE_CHANGED"),
        js("function(e) { client.com.downdogapp.client.singleton.onCastStateChanged(); }")
      )
    }
  }

  override fun play() {
    if (playerState == PAUSED) {
      remotePlayerController?.playOrPause()
    }
  }

  override fun pause() {
    if (playerState == PLAYING) {
      remotePlayerController?.playOrPause()
    }
  }

  override fun stop() {
    remotePlayerController?.stop()
    context?.endCurrentSession()
    remotePlayerController = null
    remotePlayerListener = null
  }

  override fun loadInternal(load: LoadParams) {
    val request = createRequest(load)
    if (request != null) {
      (session?.loadMedia(request) as Promise<Unit>?)
        ?.then(
          onFulfilled = {
            remotePlayerListener = js("new cast.framework.RemotePlayer()")
            fun createRemotePlayerController(@Suppress("UNUSED_PARAMETER") remotePlayer: dynamic) =
              js("new cast.framework.RemotePlayerController(remotePlayer)")
            remotePlayerController = createRemotePlayerController(remotePlayerListener)
            remotePlayerController.addEventListener(
              js("cast.framework.RemotePlayerEventType.PLAYER_STATE_CHANGED"),
              js("function(e) { client.com.downdogapp.client.singleton.onCastPlayerStateChanged(); }")
            )
            // theoretically we can send currentTime in request but it doesn't seem to work
            playerTime = load.playbackStartTime.seconds
          },
          onRejected = {
          }
        )
    }
  }

  private fun createRequest(load: LoadParams): dynamic {
    val mediaInfo: dynamic
    if (MANIFEST.castAsMp4) {
      mediaInfo = js("new chrome.cast.media.MediaInfo('', 'video/mp4')")
      mediaInfo.streamType = js("chrome.cast.media.StreamType.BUFFERED")
    } else {
      mediaInfo = js("new chrome.cast.media.MediaInfo('', 'application/x-mpegurl')")
      mediaInfo.streamType = js("chrome.cast.media.StreamType.LIVE")
      mediaInfo.hlsSegmentFormat = js("chrome.cast.media.HlsSegmentFormat.TS_AAC")
      mediaInfo.hlsVideoSegmentFormat = js("chrome.cast.media.HlsVideoSegmentFormat.MPEG2_TS")
    }
    mediaInfo.contentUrl = load.videoUrl // replaces first arg of MediaInfo constructor

    val request = js("new chrome.cast.media.LoadRequest()")
    request.media = mediaInfo
    request.autoplay = true // with autoplay=false, the video remains black which looks confusing
    return request
  }
}
